import React, {useEffect, useRef} from 'react'
import {Box, Tabs, Tab} from '@mui/material'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

interface TabDef {
  key: string
  label: string
  icon?: React.ReactElement
}

interface TabBarProps {
  tabs: TabDef[]
  activeKey: string
  onChange: (key: string) => void
  /**
   * Keep the prev/next scroll arrows visible on touch devices too, as an extra
   * affordance alongside horizontal swipe. MUI hides them on touch-capable
   * viewports by default, which can leave swipe as the only (undiscoverable)
   * way to reach tabs that overflow the viewport width. Defaults to `true` so
   * every consumer benefits without an opt-in; pass `false` to restore the old
   * touch-hidden behavior.
   */
  allowScrollButtonsMobile?: boolean
}

// How close to an edge (in px) counts as "at the edge" — accounts for sub-pixel
// scroll positions some browsers report.
const EDGE_TOLERANCE = 2
// How long (ms) after the last scroll delta to consider the scroll "settled".
const SETTLE_DEBOUNCE_MS = 150
// How long (ms) after a touch gesture ends to keep treating scroll events as
// user-driven — covers the momentum/inertia phase that keeps firing scroll
// events after the finger has already lifted.
const GESTURE_GRACE_MS = 600

export default function TabBar({tabs, activeKey, onChange, allowScrollButtonsMobile = true}: TabBarProps) {
  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    onChange(newValue)
  }

  const containerRef = useRef<HTMLDivElement>(null)

  // Makes the tab strip circular on mobile: swiping past the last tab wraps around
  // to the first one, and vice versa, instead of stopping dead at the edge.
  //
  // Only wraps when the scroll was driven by an actual touch gesture (tracked via
  // touchstart/touchend, with a grace window to cover inertial momentum after the
  // finger lifts). This avoids misfiring when MUI programmatically scrolls a newly
  // selected tab into view (e.g. tapping the last, partially-hidden tab), which
  // would otherwise immediately wrap back to the start and hide the selection.
  useEffect(() => {
    const scroller = containerRef.current?.querySelector<HTMLDivElement>('.MuiTabs-scroller')
    if (!scroller) return

    let lastScrollLeft = scroller.scrollLeft
    let settleTimer: ReturnType<typeof setTimeout> | undefined
    let gestureEndTimer: ReturnType<typeof setTimeout> | undefined
    let isGestureActive = false
    let isAutoScrolling = false

    const handleTouchStart = () => {
      isGestureActive = true
      if (gestureEndTimer) clearTimeout(gestureEndTimer)
    }

    const handleTouchEnd = () => {
      if (gestureEndTimer) clearTimeout(gestureEndTimer)
      gestureEndTimer = setTimeout(() => { isGestureActive = false }, GESTURE_GRACE_MS)
    }

    const handleScroll = () => {
      if (isAutoScrolling) return

      const current = scroller.scrollLeft
      const direction = current > lastScrollLeft ? 'forward' : current < lastScrollLeft ? 'backward' : undefined
      lastScrollLeft = current

      if (settleTimer) clearTimeout(settleTimer)
      settleTimer = setTimeout(() => {
        if (!isGestureActive) return

        const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth
        if (maxScrollLeft <= 0) return

        const wrapTo = (left: number) => {
          isAutoScrolling = true
          scroller.scrollTo({left, behavior: 'smooth'})
          setTimeout(() => {
            isAutoScrolling = false
            lastScrollLeft = scroller.scrollLeft
          }, 500)
        }

        if (direction === 'forward' && current >= maxScrollLeft - EDGE_TOLERANCE) {
          wrapTo(0)
        } else if (direction === 'backward' && current <= EDGE_TOLERANCE) {
          wrapTo(maxScrollLeft)
        }
      }, SETTLE_DEBOUNCE_MS)
    }

    scroller.addEventListener('touchstart', handleTouchStart, {passive: true})
    scroller.addEventListener('touchend', handleTouchEnd, {passive: true})
    scroller.addEventListener('touchcancel', handleTouchEnd, {passive: true})
    scroller.addEventListener('scroll', handleScroll, {passive: true})

    return () => {
      scroller.removeEventListener('touchstart', handleTouchStart)
      scroller.removeEventListener('touchend', handleTouchEnd)
      scroller.removeEventListener('touchcancel', handleTouchEnd)
      scroller.removeEventListener('scroll', handleScroll)
      if (settleTimer) clearTimeout(settleTimer)
      if (gestureEndTimer) clearTimeout(gestureEndTimer)
    }
  }, [tabs.length])

  return (
    <Box ref={containerRef}>
      <Tabs
        value={activeKey}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile={allowScrollButtonsMobile}
        sx={{
          // MUI's scrollable Tabs container already sets `overflow-x: auto`, but on
          // iOS Safari that alone can render a janky (non-momentum) drag instead of a
          // smooth swipe, and a swipe that reaches the start/end can bubble into the
          // page's own vertical scroll. Both are fixed at the `.MuiTabs-scroller`
          // level so every consumer of TabBar gets fluid horizontal swipe for free.
          '& .MuiTabs-scroller': {
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
          },
          '& .MuiTabs-scrollButtons': {
            color: NEUTRAL_SCALE[500],
          },
          '& .MuiTabs-scrollButtons.Mui-disabled': {
            opacity: 0,
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            label={tab.label}
            icon={tab.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>
    </Box>
  )
}